import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.regexp.flags.js";
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { createScope, FeatureFlags as FeatureFlags$1 } from '@carbon/feature-flags';
import PropTypes from 'prop-types';
import React__default, { useContext, useState, useRef, useEffect, createContext } from 'react';
import deprecate from '../../prop-types/deprecate.js';

/**
 * Our FeatureFlagContext is used alongside the FeatureFlags component to enable
 * or disable feature flags in a given React tree
 */
var FeatureFlagContext = /*#__PURE__*/createContext(FeatureFlags$1);

/**
 * Supports an object of feature flag values with the `flags` prop, merging them
 * along with the current `FeatureFlagContext` to provide consumers to check if
 * a feature flag is enabled or disabled in a given React tree
 */
function FeatureFlags(_ref) {
  var children = _ref.children,
    _ref$flags = _ref.flags,
    flags = _ref$flags === void 0 ? {} : _ref$flags,
    _ref$enableV12TileDef = _ref.enableV12TileDefaultIcons,
    enableV12TileDefaultIcons = _ref$enableV12TileDef === void 0 ? false : _ref$enableV12TileDef,
    _ref$enableV12TileRad = _ref.enableV12TileRadioIcons,
    enableV12TileRadioIcons = _ref$enableV12TileRad === void 0 ? false : _ref$enableV12TileRad,
    _ref$enableV12Overflo = _ref.enableV12Overflowmenu,
    enableV12Overflowmenu = _ref$enableV12Overflo === void 0 ? false : _ref$enableV12Overflo,
    _ref$enableTreeviewCo = _ref.enableTreeviewControllable,
    enableTreeviewControllable = _ref$enableTreeviewCo === void 0 ? false : _ref$enableTreeviewCo,
    _ref$enableExperiment = _ref.enableExperimentalFocusWrapWithoutSentinels,
    enableExperimentalFocusWrapWithoutSentinels = _ref$enableExperiment === void 0 ? false : _ref$enableExperiment,
    _ref$enableDialogElem = _ref.enableDialogElement,
    enableDialogElement = _ref$enableDialogElem === void 0 ? false : _ref$enableDialogElem,
    _ref$enableV12Dynamic = _ref.enableV12DynamicFloatingStyles,
    enableV12DynamicFloatingStyles = _ref$enableV12Dynamic === void 0 ? false : _ref$enableV12Dynamic;
  var parentScope = useContext(FeatureFlagContext);
  var _useState = useState(parentScope),
    _useState2 = _slicedToArray(_useState, 2),
    prevParentScope = _useState2[0],
    setPrevParentScope = _useState2[1];
  var combinedFlags = _objectSpread({
    'enable-v12-tile-default-icons': enableV12TileDefaultIcons,
    'enable-v12-tile-radio-icons': enableV12TileRadioIcons,
    'enable-v12-overflowmenu': enableV12Overflowmenu,
    'enable-treeview-controllable': enableTreeviewControllable,
    'enable-experimental-focus-wrap-without-sentinels': enableExperimentalFocusWrapWithoutSentinels,
    'enable-dialog-element': enableDialogElement,
    'enable-v12-dynamic-floating-styles': enableV12DynamicFloatingStyles
  }, flags);
  var _useState3 = useState(function () {
      var scope = createScope(combinedFlags);
      scope.mergeWithScope(parentScope);
      return scope;
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    scope = _useState4[0],
    updateScope = _useState4[1];
  if (parentScope !== prevParentScope) {
    var _scope = createScope(combinedFlags);
    _scope.mergeWithScope(parentScope);
    updateScope(_scope);
    setPrevParentScope(parentScope);
  }

  // We use a custom hook to detect if any of the keys or their values change
  // for flags that are passed in. If they have changed, then we re-create the
  // FeatureFlagScope using the new flags
  useChangedValue(combinedFlags, isEqual, function (changedFlags) {
    var scope = createScope(changedFlags);
    scope.mergeWithScope(parentScope);
    updateScope(scope);
  });
  return /*#__PURE__*/React__default.createElement(FeatureFlagContext.Provider, {
    value: scope
  }, children);
}
FeatureFlags.propTypes = {
  children: PropTypes.node,
  /**
   * Provide the feature flags to enabled or disabled in the current Rea,ct tree
   */
  flags: deprecate(PropTypes.objectOf(PropTypes.bool), 'The `flags` prop for `FeatureFlag` has ' + 'been deprecated. Please run the `featureflag-deprecate-flags-prop` codemod to migrate to individual boolean props.' + "npx @carbon/upgrade migrate featureflag-deprecate-flags-prop --write"),
  enableV12TileDefaultIcons: PropTypes.bool,
  enableV12TileRadioIcons: PropTypes.bool,
  enableV12Overflowmenu: PropTypes.bool,
  enableTreeviewControllable: PropTypes.bool,
  enableExperimentalFocusWrapWithoutSentinels: PropTypes.bool,
  enableDialogElement: PropTypes.bool,
  enableV12DynamicFloatingStyles: PropTypes.bool
};

/**
 * This hook will store previous versions of the given `value` and compare the
 * current value to the previous one using the `compare` function. If the
 * compare function returns true, then the given `callback` is invoked in an
 * effect.
 *
 * @param {any} value
 * @param {Function} compare
 * @param {Function} callback
 */
function useChangedValue(value, compare, callback) {
  var initialRender = useRef(false);
  var savedCallback = useRef(callback);
  var _useState5 = useState(value),
    _useState6 = _slicedToArray(_useState5, 2),
    prevValue = _useState6[0],
    setPrevValue = _useState6[1];
  if (!compare(prevValue, value)) {
    setPrevValue(value);
  }
  useEffect(function () {
    savedCallback.current = callback;
  });
  useEffect(function () {
    // We only want the callback triggered after the first render
    if (initialRender.current) {
      savedCallback.current(prevValue);
    }
  }, [prevValue]);
  useEffect(function () {
    initialRender.current = true;
  }, []);
}

/**
 * Access whether a given flag is enabled or disabled in a given
 * FeatureFlagContext
 *
 * @returns {boolean}
 */
function useFeatureFlag(flag) {
  var scope = useContext(FeatureFlagContext);
  return scope.enabled(flag);
}

/**
 * Access all feature flag information for the given FeatureFlagContext
 *
 * @returns {FeatureFlagScope}
 */
function useFeatureFlags() {
  return useContext(FeatureFlagContext);
}

/**
 * Compare two objects and determine if they are equal. This is a shallow
 * comparison since the objects we are comparing are objects with boolean flags
 * from the flags prop in the `FeatureFlags` component
 *
 * @param {object} a
 * @param {object} b
 * @returns {boolean}
 */
function isEqual(a, b) {
  if (a === b) {
    return true;
  }
  for (var _i = 0, _Object$keys = Object.keys(a); _i < _Object$keys.length; _i++) {
    var key = _Object$keys[_i];
    if (a[key] !== b[key]) {
      return false;
    }
  }
  for (var _i2 = 0, _Object$keys2 = Object.keys(b); _i2 < _Object$keys2.length; _i2++) {
    var _key = _Object$keys2[_i2];
    if (b[_key] !== a[_key]) {
      return false;
    }
  }
  return true;
}
export { FeatureFlagContext, FeatureFlags, useFeatureFlag, useFeatureFlags };